'use client';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { withStyles } from 'tss-react/mui';

import { black, neutral7 } from '../../colors';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import Avatar from '../Avatar';
import Button from '../Button';
import Divider from '../Divider';
import ListItemIcon from '../ListItemIcon';
import ListItemText from '../ListItemText';
import ListSubheader from '../ListSubheader';
import MenuItem from '../MenuItem';
import NavigationBarButton from '../NavigationBar/NavigationBarButton';
import PopperMenu from '../PopperMenu';
import Typography from '../Typography';

export const styles = ({ idsTokens = idsTokensDefault }) => ({
  menu: {
    '& div.MuiListItemIcon-root': {
      minWidth: 'unset',
    },
    '& .MuiMenuItem-root:active': {
      /**
       * @TODO this should become unnecessary when the List component is updated
       * to not have a narrower borderBottom when pressed
       */
      borderBottomWidth: '2px !important',
    },
  },
  arrow: {
    marginLeft: 8,
    fontSize: 16,
  },
  title: {
    fontWeight: idsTokens.typography.font.weight.semibold,
    fontSize: idsTokens.typography.font.size.base,
    lineHeight: idsTokens.typography.lineHeight.base,
  },
  menuHeader: {
    outline: 'none',
    padding: '8px 16px 8px 16px',
    '& .MuiListItemText-root': {
      color: idsTokens.listItem.default.color.text.label.enabled,
      '& .MuiListItemText-primary': {
        fontWeight: idsTokens.typography.font.weight.medium,
        fontSize: idsTokens.typography.font.size.base,
        lineHeight: idsTokens.typography.lineHeight.base,
      },
      '& .MuiListItemText-secondary': {
        fontSize: idsTokens.typography.font.size.sm,
        lineHeight: idsTokens.typography.lineHeight.sm,
      },
    },
  },
  email: {
    color: neutral7,
    fontSize: idsTokens.typography.font.size.sm,
    lineHeight: idsTokens.typography.lineHeight.sm,
  },
  dividerContainer: {
    paddingRight: 0,
    marginTop: 4,
    marginBottom: 4,
  },
  icon: {
    color: idsTokens.listItem.default.color.text.label.enabled,
    marginLeft: 5,
    fontSize: 19.22,
    padding: 0.4,
    boxSizing: 'content-box',
    '.MuiMenuItem-root:hover &': {
      color: idsTokens.listItem.default.color.text.label.hover,
    },
    '.MuiMenuItem-root:focus-visible &': {
      color: idsTokens.listItem.default.color.text.label.focus,
    },
    '.MuiMenuItem-root:active &': {
      color: idsTokens.listItem.default.color.text.label.pressed,
    },
  },
  logoutButton: {
    margin: '8px 16px 8px 16px',
    width: 248,
    boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.04)',
    overflow: 'hidden',
    fontSize: 14,
  },
  paper: {
    boxSizing: 'border-box',
    width: 280,
  },
  selected: {
    fontWeight: 500,
    color: black,
  },
  accent: {
    gap: idsTokens.navbar.accent.spacing.menuItem.gap,
    paddingTop: idsTokens.navbar.accent.spacing.menuItem.top,
    paddingRight: idsTokens.navbar.accent.spacing.menuItem.right,
    paddingBottom: idsTokens.navbar.accent.spacing.menuItem.bottom,
    paddingLeft: idsTokens.navbar.accent.spacing.menuItem.left,
  },
  brand: {
    gap: idsTokens.navbar.brand.spacing.menuItem.gap,
    paddingTop: idsTokens.navbar.brand.spacing.menuItem.top,
    paddingRight: idsTokens.navbar.brand.spacing.menuItem.right,
    paddingBottom: idsTokens.navbar.brand.spacing.menuItem.bottom,
    paddingLeft: idsTokens.navbar.brand.spacing.menuItem.left,
  },
  classic: {
    gap: idsTokens.navbar.classic.spacing.menuItem.gap,
    paddingTop: idsTokens.navbar.classic.spacing.menuItem.top,
    paddingRight: idsTokens.navbar.classic.spacing.menuItem.right,
    paddingBottom: idsTokens.navbar.classic.spacing.menuItem.bottom,
    paddingLeft: idsTokens.navbar.classic.spacing.menuItem.left,
  },
  inverse: {
    gap: idsTokens.navbar.inverse.spacing.menuItem.gap,
    paddingTop: idsTokens.navbar.inverse.spacing.menuItem.top,
    paddingRight: idsTokens.navbar.inverse.spacing.menuItem.right,
    paddingBottom: idsTokens.navbar.inverse.spacing.menuItem.bottom,
    paddingLeft: idsTokens.navbar.inverse.spacing.menuItem.left,
  },
  neutral: {
    gap: idsTokens.navbar.neutral.spacing.menuItem.gap,
    paddingTop: idsTokens.navbar.neutral.spacing.menuItem.top,
    paddingRight: idsTokens.navbar.neutral.spacing.menuItem.right,
    paddingBottom: idsTokens.navbar.neutral.spacing.menuItem.bottom,
    paddingLeft: idsTokens.navbar.neutral.spacing.menuItem.left,
  },
  brandButton: {
    color: idsTokens.navbar.brand.color.content.item.enabled,
  },
  accentButton: {
    color: idsTokens.navbar.accent.color.content.item.enabled,
  },
  classicButton: {
    color: idsTokens.navbar.classic.color.content.item.enabled,
  },
  neutralButton: {
    color: idsTokens.navbar.neutral.color.content.item.enabled,
  },
  inverseButton: {
    color: idsTokens.navbar.inverse.color.content.item.enabled,
  },
});

export class ProfileMenu extends React.Component {
  state = {
    anchorEl: null,
    open: false,
  };

  handleClick = (e = {}) => {
    if (!this.props.menuItems && this.props.onClick) {
      this.props.onClick();
    } else {
      this.setState({ open: true, anchorEl: e.currentTarget });
    }
  };

  handleRequestClose = () => {
    this.setState({ open: false });
  };

  render() {
    const {
      classes,
      onClick,
      menuItems,
      title,
      logoutText = 'Log out',
      email,
      name,
      icon,
      checkIsActive,
      hideLogout,
      logoutButtonProps,
      extraContent,
      variant,
      placement = 'bottom-end',
      PopperProps,
      ...rest
    } = this.props;
    const { open, anchorEl } = this.state;
    const hasMenuItems = !!menuItems;
    const variantClass = classes[variant];

    return (
      <>
        <NavigationBarButton
          open={open}
          text={name}
          icon={
            !icon && typeof name === 'string' ? (
              <Avatar size="small" stateless>
                {name &&
                  name
                    .split(' ')
                    .map((name) => name[0])
                    .join('')}
              </Avatar>
            ) : (
              icon
            )
          }
          className={clsx(classes[`${variant}Button`])}
          hideArrow={!name}
          hasMenuItems={hasMenuItems}
          handleClick={this.handleClick}
          {...rest}
          variant={variant}
        />

        {hasMenuItems && open && (
          <PopperMenu
            open={open}
            onClose={this.handleRequestClose}
            anchorEl={anchorEl}
            placement={placement}
            {...PopperProps}
            className={clsx(classes.menu, PopperProps?.className)}
            classes={{
              ...PopperProps?.classes,
              paper: clsx(classes.paper, PopperProps?.classes?.paper),
            }}
            popperOptions={{ strategy: 'fixed', ...PopperProps?.popperOptions }}
            modifiers={[
              {
                name: 'offset',
                options: { offset: [0, 8] },
              },
              ...(PopperProps?.modifiers ?? []),
            ]}
          >
            {(title || email) && (
              <ListSubheader disableSticky className={classes.menuHeader}>
                <Typography
                  variant="bodyDefault"
                  emphasis="low"
                  sx={{
                    '&': { color: idsTokensDefault.listItem.default.color.text.label.enabled },
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="bodySmall"
                  emphasis="low"
                  sx={{
                    '&': { color: idsTokensDefault.listItem.default.color.text.sub.enabled },
                  }}
                >
                  {email}
                </Typography>
              </ListSubheader>
            )}
            {(title || email) && menuItems.length > 0 && (
              <ListSubheader disableSticky className={classes.dividerContainer}>
                <Divider type="light" />
              </ListSubheader>
            )}
            {menuItems.map((item, i) => {
              const MenuIcon = item.icon;
              return (
                <MenuItem
                  className={clsx(
                    variantClass,
                    item.className,
                    (item.active ?? checkIsActive?.(item)) && classes.selected
                  )}
                  style={item.style}
                  onClick={() => {
                    item.onClick && item.onClick();
                    onClick && onClick(item);
                    this.handleRequestClose();
                  }}
                  key={i}
                  disableRipple
                >
                  {item.icon && (
                    <ListItemIcon>
                      <MenuIcon className={classes.icon} />
                    </ListItemIcon>
                  )}
                  <ListItemText primary={item.text} disableTypography />
                </MenuItem>
              );
            })}
            {!hideLogout && (
              <Button
                className={classes.logoutButton}
                variant="secondary"
                onClick={() => {
                  (logoutButtonProps?.onClick &&
                    logoutButtonProps.onClick({ text: logoutText, ...logoutButtonProps })) ||
                    (onClick && onClick({ text: logoutText, ...logoutButtonProps }));
                  this.handleRequestClose();
                }}
                {...logoutButtonProps}
              >
                {logoutButtonProps?.text ?? logoutText}
              </Button>
            )}
            {extraContent}
          </PopperMenu>
        )}
      </>
    );
  }
}

ProfileMenu.propTypes = {
  /** JSS styles object.
   * @ignore */
  classes: PropTypes.object.isRequired,
  /** A function that determines which menu is styled as active. */
  checkIsActive: PropTypes.func,
  /** The email address of the active user. */
  email: PropTypes.node,
  /** Custom content shown at the end of the menu. */
  extraContent: PropTypes.node,
  /** If `true`, the logout button is hidden. */
  hideLogout: PropTypes.bool,
  /** Add an icon to the profile menu button. */
  icon: PropTypes.element,
  /** Props applied to the logout button. */
  logoutButtonProps: PropTypes.shape({
    /** URL of the page the link goes to. */
    href: PropTypes.string,
    /** Callback fired when the component is clicked. */
    onClick: PropTypes.func,
    /** Path of the menu item. Used in `checkIsActive`. */
    pathname: PropTypes.string,
    /** Text displayed in the logout button. */
    text: PropTypes.node,
  }),
  /** Text displayed in the logout button. */
  logoutText: PropTypes.node,
  /** The list of navigation items. */
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      /** Add an icon to the menu item. */
      icon: PropTypes.elementType,
      /** Path of the menu item. Used in `checkIsActive`. */
      pathname: PropTypes.string,
      /** The content of the menu item. */
      text: PropTypes.node,
    })
  ),
  /** The name of the active user. */
  name: PropTypes.node,
  /** Callback fired when a navigation item is clicked. */
  onClick: PropTypes.func,
  /** Props applied to the `Popper` component. */
  PopperProps: PropTypes.object,
  /** The business title of the active user. */
  title: PropTypes.node,
};

export default withStyles(ProfileMenu, styles);
